import { API_BASE, apiGet, apiPost, apiPut } from "./api";
import { auth } from "./firebase";
import { getValidToken } from "./token-store";

export interface AffiliateRecord {
  id: string;
  userId: string;
  email: string;
  name: string;
  photoUrl: string | null;
  socialInstagram: string | null;
  socialYoutube: string | null;
  socialTiktok: string | null;
  socialLinkedin: string | null;
  socialTwitter: string | null;
  socialWebsite: string | null;
  bio: string | null;
  phone: string | null;
  secondaryEmail: string | null;
  referralCode: string | null;
  status: "pending" | "approved" | "rejected";
  countSignedUp: number;
  countOnboarded: number;
  countPaying: number;
  countChurned: number;
  createdAt: string;
  updatedAt: string;
  shareLink?: string;
}

export interface AffiliateStatsBucket {
  last1yr: number;
  last7d: number;
  last30d: number;
  last90d: number;
}

export interface AffiliateStats {
  signedUp: AffiliateStatsBucket;
  onboarded: AffiliateStatsBucket;
  paying: AffiliateStatsBucket;
  churned: AffiliateStatsBucket;
}

export interface TrendPoint {
  date: string;
  signedUp: number;
  onboarded: number;
  paying: number;
  churned: number;
}

export interface AffiliateTrends {
  points: TrendPoint[];
  granularity: "day" | "month";
}

export function getAffiliateStatus() {
  return apiGet<AffiliateRecord | null>("/api/affiliate/me");
}

export function getAffiliateStats() {
  return apiGet<AffiliateStats>("/api/affiliate/stats");
}

export function getAffiliateTrends(window: "7d" | "30d" | "90d" | "1yr") {
  return apiGet<AffiliateTrends>(`/api/affiliate/trends?window=${window}`);
}

export function submitApplication(data: {
  name: string;
  photoUrl?: string;
  phone?: string;
  secondaryEmail?: string;
  socials?: {
    instagram?: string;
    youtube?: string;
    tiktok?: string;
    linkedin?: string;
    twitter?: string;
    website?: string;
  };
}) {
  return apiPost<AffiliateRecord>("/api/affiliate/apply", data);
}

export function updateAffiliate(data: {
  name?: string;
  bio?: string;
  phone?: string;
  secondaryEmail?: string;
  socials?: {
    instagram?: string;
    youtube?: string;
    tiktok?: string;
    linkedin?: string;
    twitter?: string;
    website?: string;
  };
}) {
  return apiPut<AffiliateRecord>("/api/affiliate/me", data);
}

export function resolvePhotoUrl(photoUrl: string | null): string {
  if (!photoUrl) return "";
  if (photoUrl.startsWith("http") || photoUrl.startsWith("blob:")) return photoUrl;
  return `${API_BASE}${photoUrl}`;
}

export async function uploadPhoto(file: File): Promise<{ url: string }> {
  const token = auth.currentUser
    ? await auth.currentUser.getIdToken()
    : await getValidToken();
  if (!token) throw new Error("Not authenticated");

  const formData = new FormData();
  formData.append("photo", file);

  const res = await fetch(`${API_BASE}/api/affiliate/photo`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  const json = (await res.json()) as {
    success: boolean;
    data?: { url: string };
    error?: { message: string };
  };
  if (!json.success) throw new Error(json.error?.message ?? "Upload failed");
  return json.data!;
}
